// ─── Live notification toasts (2026-07-30) ──────────────────────────────────
// Admin + Confirmation Agent only, and ON THE ADMIN PAGES ONLY. Mounted ONCE at
// app level (AdminLayout remounts on every admin page; a toast must survive the
// hop), but the feed behind it runs only under /admin (lib/useNotifications.ts):
// a customer page — the 3D preview, the editor, an upload or printer link, the
// flow — draws nothing and opens nothing, even with a staff session in that
// browser (owner, 2026-10-03). That includes the editor when an agent works in
// it: their alerts wait in the bell and on push.
//
// MUST NOT INTERRUPT. Specifically:
//   • no modal, no autofocus, nothing focusable stealing the caret mid-edit
//   • `aria-live="polite"` (not assertive) so a screen reader finishes the
//     current utterance first
//   • the stack wrapper is `pointer-events: none`; only each card re-enables
//     pointer events, so the toast can never swallow a click on the page beneath
//     — important over the editor canvas, where a swallowed drag loses work
//   • fixed to the top-right and clear of the sticky admin header; on a phone it
//     sits top-centre, never over the bottom action bars
import { useNavigate } from 'react-router-dom';
import {
  useNotifications,
  dismissToast,
  holdToast,
  resumeToast,
} from '@/lib/useNotifications';
import type { AppNotification, NotificationKind } from '@/lib/api';
import { notificationHref } from '../lib/notificationHref';

const ACCENT: Record<NotificationKind, { bar: string; dot: string }> = {
  // status tokens (index.css): identical in light, readable at night
  'lead.new':         { bar: 'var(--color-adm-gold)', dot: 'rgba(201,162,74,.16)' },
  'order.new':        { bar: 'var(--color-status-done)', dot: 'rgba(21,128,61,.14)' },
  'delivery.problem': { bar: 'var(--color-status-stuck)', dot: 'rgba(185,28,28,.13)' },
  'message.failed': { bar: 'var(--color-status-stuck)', dot: 'rgba(185,28,28,.13)' },
  alert:            { bar: 'var(--color-status-waiting)', dot: 'rgba(180,83,9,.13)' },
};

const ICON: Record<NotificationKind, string> = {
  'lead.new': '☎',
  'order.new': '✓',
  'delivery.problem': '!',
  'message.failed': '!',
  alert: '!',
};

export default function NotificationToasts() {
  // PURE RENDER of the store's queue — no local state, no timers, no clock.
  //
  // WHY THIS SHAPE (2026-08-13). The previous host re-derived its own list from
  // `items` into a local `useState`, admitting a row only if
  // `Date.parse(n.created_at) >= mountedAt`. Two silent killers lived in that
  // one line, and both fail exactly the way this bug presented — bell fine,
  // unread count fine, feed fine, popup never:
  //   1. realtime hands over the RAW Postgres timestamptz text
  //      ("2026-08-13 01:02:39.350734+00": space separator, `+00` offset —
  //      realtime-js passes timestamptz through untouched). That string is not
  //      the ES Date Time String Format, so Date.parse may return NaN, and the
  //      `Number.isFinite` guard then drops the row without a sound.
  //   2. comparing a SERVER timestamp against the CLIENT's Date.now() means a
  //      wall clock a few seconds fast suppresses every toast forever.
  // Meanwhile the store already owned a `toasts` queue, wrote to it on every
  // realtime INSERT, and NOTHING READ IT. So the working half was never
  // connected to the rendering half.
  //
  // Now the toast rides the same store→listener→render path as the bell badge,
  // which is proven live. Freshness is decided by an id set in the store, never
  // by a date.
  //
  // `enabled` is false outside /admin — the sign-in screens included (a toast
  // there read as « still signed in after logging out », owner 2026-10-01) and
  // every customer page (2026-10-03).
  const { toasts, markRead, enabled } = useNotifications();
  const navigate = useNavigate();

  if (!enabled || toasts.length === 0) return null;

  const open = (n: AppNotification) => {
    markRead([n.id]);   // also retires the toast
    dismissToast(n.id);
    const href = notificationHref(n);
    if (href) navigate(href);
  };

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="fixed z-[1000] pointer-events-none flex flex-col gap-2
                 top-[calc(var(--adm-header-h)+12px)] left-3 right-3
                 sm:left-auto sm:right-4 sm:w-[340px]"
    >
      {toasts.map((n) => {
        const accent = ACCENT[n.kind] ?? ACCENT['order.new'];
        return (
          <button
            key={n.id}
            type="button"
            onClick={() => open(n)}
            onMouseEnter={() => holdToast(n.id)}
            onMouseLeave={() => resumeToast(n.id)}
            className="adm-toast pointer-events-auto w-full text-left flex items-start gap-2.5
                       rounded-2xl border px-3.5 py-3 cursor-pointer"
            style={{
              background: 'var(--color-adm-chrome)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderColor: 'rgba(201,162,74,0.22)',
              boxShadow: 'var(--adm-toast-shadow)',
              borderLeft: `3px solid ${accent.bar}`,
            }}
          >
            <span
              aria-hidden="true"
              className="shrink-0 w-6 h-6 rounded-full grid place-items-center text-[12px] font-bold"
              style={{ background: accent.dot, color: accent.bar }}
            >
              {ICON[n.kind] ?? '•'}
            </span>
            <span className="min-w-0 flex-1">
              <span
                className="block text-[13.5px] font-semibold leading-tight truncate"
                style={{ fontFamily: "'Fraunces', Georgia, serif", color: 'var(--color-text-primary)' }}
              >
                {n.title}
              </span>
              {n.body && (
                <span className="block text-[11.5px] text-gray-500 leading-snug mt-0.5 line-clamp-2">
                  {n.body}
                </span>
              )}
            </span>
            <span
              aria-hidden="true"
              onClick={(e) => { e.stopPropagation(); dismissToast(n.id); }}
              className="shrink-0 -mr-1 -mt-1 w-6 h-6 grid place-items-center rounded-lg
                         text-gray-300 hover:text-gray-600 text-[15px] leading-none"
            >
              ×
            </span>
          </button>
        );
      })}
    </div>
  );
}
